/**
 * Inline Delete Confirmation
 * 
 * Drawer-native delete confirmation that appears inline within the note row
 * No overlay, no modal - just a smooth transition of the row itself
 */

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface InlineDeleteConfirmProps {
  isOpen: boolean;
  title: string;
  type: 'note' | 'task';
  onConfirm: () => void;
  onCancel: () => void;
  className?: string;
}

/**
 * Inline confirmation row that replaces the note/task content
 * Smooth layout animation with Framer Motion
 */
export function InlineDeleteConfirm({
  isOpen,
  title,
  type,
  onConfirm,
  onCancel,
  className,
}: InlineDeleteConfirmProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, onConfirm]);

  // Auto-focus confirm button when opened
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      // Small delay to ensure smooth animation
      setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 150);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30,
        opacity: { duration: 0.15 },
      }}
      className={cn(
        'rounded-2xl border bg-card/95 backdrop-blur',
        'px-4 py-3 flex items-center justify-between gap-3',
        'shadow-sm',
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Left: Icon + Message */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            Delete {type}?
          </p>
          {title && (
            <p className="text-xs text-muted-foreground truncate">
              "{title}"
            </p>
          )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          className="h-8 text-xs"
        >
          Cancel
        </Button>
        <Button
          ref={confirmButtonRef}
          variant="destructive"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onConfirm();
          }}
          className="h-8 text-xs"
        >
          Delete
        </Button>
      </div>
    </motion.div>
  );
}
